import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  Button
} from "@mui/material";
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import BackupIcon from '@mui/icons-material/Backup';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StorageIcon from '@mui/icons-material/Storage';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { UnidadesApi, IDashboardUnidades } from "../api/unidadesApi";

interface DashboardUnidadesProps {
  onNavigateToClientes?: () => void;
}

export default function DashboardUnidades({ onNavigateToClientes }: DashboardUnidadesProps) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IDashboardUnidades | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setErro(null);
      const dashboardData = await UnidadesApi.getDashboard();
      setData(dashboardData);
    } catch (e: any) {
      console.error(e);
      setErro("Não foi possível carregar as informações do dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(valor);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (erro || !data) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error" variant="h6" gutterBottom>
          {erro || "Erro inesperado."}
        </Typography>
        <Button variant="contained" color="primary" onClick={carregarDados}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: "text.primary" }}>
        Dashboard de Revenda
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Faturamento */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: "16px",
              boxShadow: theme.palette.mode === 'dark' ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.05)",
              background: theme.palette.mode === 'dark' ? "linear-gradient(135deg, #1e293b, #0f172a)" : "#ffffff",
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  FATURAMENTO MENSAL
                </Typography>
                <AvatarCard icon={<MonetizationOnIcon sx={{ color: "#10b981" }} />} color="rgba(16, 185, 129, 0.1)" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                {formatarMoeda(data.faturamentoTotal)}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
                Total cobrado dos clientes ativos
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Comissão */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: "16px",
              boxShadow: theme.palette.mode === 'dark' ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.05)",
              background: theme.palette.mode === 'dark' ? "linear-gradient(135deg, #1e293b, #0f172a)" : "#ffffff",
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  SUA COMISSÃO (80%)
                </Typography>
                <AvatarCard icon={<MonetizationOnIcon sx={{ color: "#3b82f6" }} />} color="rgba(59, 130, 246, 0.1)" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                {formatarMoeda(data.comissaoTotal)}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
                Comissão recebida das mensalidades
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Clientes */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={onNavigateToClientes}
            sx={{
              borderRadius: "16px",
              cursor: onNavigateToClientes ? "pointer" : "default",
              transition: "transform 0.2s, box-shadow 0.2s",
              '&:hover': onNavigateToClientes ? {
                transform: "translateY(-4px)",
                boxShadow: theme.palette.mode === 'dark' ? "0 10px 25px rgba(0,0,0,0.6)" : "0 10px 25px rgba(0,0,0,0.1)"
              } : {},
              boxShadow: theme.palette.mode === 'dark' ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.05)",
              background: theme.palette.mode === 'dark' ? "linear-gradient(135deg, #1e293b, #0f172a)" : "#ffffff",
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  CLIENTES ATIVOS
                </Typography>
                <AvatarCard icon={<PeopleIcon sx={{ color: "#a855f7" }} />} color="rgba(168, 85, 247, 0.1)" />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                {data.clientesAtivos}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
                Carteira de revenda ativa
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Saúde consolidada */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: "16px",
              boxShadow: theme.palette.mode === 'dark' ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.05)",
              background: theme.palette.mode === 'dark' ? "linear-gradient(135deg, #1e293b, #0f172a)" : "#ffffff",
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  SAÚDE DOS BACKUPS
                </Typography>
                <AvatarCard 
                  icon={<BackupIcon sx={{ color: data.taxaSucessoConsolidada >= 95 ? "#10b981" : data.taxaSucessoConsolidada >= 85 ? "#f59e0b" : "#ef4444" }} />} 
                  color={data.taxaSucessoConsolidada >= 95 ? "rgba(16, 185, 129, 0.1)" : data.taxaSucessoConsolidada >= 85 ? "rgba(245, 158, 11, 0.1)" : "rgba(239, 68, 68, 0.1)"} 
                />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                {data.taxaSucessoConsolidada}%
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
                Taxa de sucesso consolidada (7d)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Alertas de Backups */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: "16px", minHeight: "350px", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ErrorOutlineIcon sx={{ color: "#ef4444", mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                  Alertas Recentes de Backup
                </Typography>
              </Box>
              {data.alertasBackup.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "240px" }}>
                  <CheckCircleOutlineIcon sx={{ color: "#10b981", fontSize: "48px", mb: 1 }} />
                  <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 600 }}>
                    Nenhum alerta de backup
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    Todos os clientes estão com os backups saudáveis.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: "250px", overflow: "auto" }}>
                  {data.alertasBackup.map((alerta, i) => (
                    <ListItem
                      key={i}
                      sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:last-child': { borderBottom: 'none' },
                        px: 1
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {alerta.nomeCliente}
                            </Typography>
                            <Chip size="small" label="Falha" color="error" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }} />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Tarefa: {alerta.tarefaNome} • {new Date(alerta.dataHora).toLocaleString("pt-BR")}
                            </Typography>
                            <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
                              Erro: {alerta.erro}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Alertas de Discos */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: "16px", minHeight: "350px", border: `1px solid ${theme.palette.divider}`, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <StorageIcon sx={{ color: "#f59e0b", mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
                  Espaço em Disco Crítico
                </Typography>
              </Box>
              {data.alertasEspaco.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "240px" }}>
                  <CheckCircleOutlineIcon sx={{ color: "#10b981", fontSize: "48px", mb: 1 }} />
                  <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 600 }}>
                    Nenhum disco com espaço crítico
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    Todos os discos dos clientes possuem espaço livre confortável.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: "250px", overflow: "auto" }}>
                  {data.alertasEspaco.map((alerta, i) => (
                    <ListItem
                      key={i}
                      sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:last-child': { borderBottom: 'none' },
                        px: 1
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <WarningAmberIcon sx={{ color: "#f59e0b" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {alerta.nomeCliente} (Unidade {alerta.unidade}:)
                            </Typography>
                            <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 700 }}>
                              {alerta.percentualUso}% Usado
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            Capacidade Total: {Math.round(alerta.capacidadeTotal / 1024 * 10) / 10} GB • Livre: {Math.round(alerta.capacidadeLivre / 1024 * 10) / 10} GB
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function AvatarCard({ icon, color }: { icon: React.ReactNode; color: string }) {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        backgroundColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {icon}
    </Box>
  );
}
